import { Icon } from "@/components/Icon/Icon";
import { logEvent } from "@/lib/eventLogger";
import type { FormulationEntry } from "@/utils/types/compound";
import type {
  CompareCatalog,
  CompareFormula,
  ComputeCompareStats,
} from "@/utils/types/compare";
import { CompareControl } from "@/components/Workbench/Compare";
import {
  AutoFormulateButton,
  type AutoFormulateRunOpts,
} from "./AutoFormulate/AutoFormulateButton";
import { FlaskMoreMenu } from "./AutoFormulate/FlaskMoreMenu";
import type { VersionDetail } from "@/utils/types/flavorVersion";

// Current Flask header actions.
//
// Layout (left → right):
//   [Auto-Formulate ▾] · Composition · Compare · ⋯
//
// Auto-Formulate is the proprietary primary action — a split-button with a
// default mode (Optimize Loss) on the body and a dropdown caret for Optimize
// Cost / From prompt. On PILOT deployments (VITE_NF_AF_PRO_MODES unset/false),
// the non-default modes surface as PRO-tier locked rows. Composition is a
// toggle. Compare keeps its standalone control. History / Import / Export
// collapse into the ⋯ overflow menu since they're occasional-use.
type FlaskToolbarProps = {
  formulation: FormulationEntry[];
  foundation: FormulationEntry[];
  // Composition toggle — only rendered when the active target carries one.
  showComposition: boolean;
  onToggleComposition?: () => void;
  hasComposition: boolean;
  // Pass-through ids used by HistoryPopover + Import + Export.
  flavorId: string | null;
  formulaId: number | null;
  // Export filename pieces.
  flavorName?: string;
  formulaName?: string;
  // Compare wiring.
  compareCatalog?: CompareCatalog;
  compareSelected?: CompareFormula[];
  onCompareToggle?: (f: CompareFormula) => void;
  onCompareClear?: () => void;
  computeCompareStats?: ComputeCompareStats;
  /** Forwarded to <CompareControl>. */
  suggestionsLoading?: boolean;
  /** Fired after a successful version restore. */
  onRestoreSuccess?: (detail: VersionDetail) => void;
  /** Fired after a successful import commit. */
  onImportCommitSuccess?: (detail: VersionDetail) => void;
  // Auto-Formulate wiring.
  onAutoFormulate: (opts: AutoFormulateRunOpts) => void;
  autoFormulateDisabled?: boolean;
  /** Hover tooltip surfaced when `autoFormulateDisabled` is true. */
  autoFormulateDisabledReason?: string;
};

export function FlaskToolbar({
  formulation,
  foundation,
  showComposition,
  onToggleComposition,
  hasComposition,
  flavorId,
  formulaId,
  flavorName,
  formulaName,
  compareCatalog,
  compareSelected = [],
  onCompareToggle,
  onCompareClear,
  computeCompareStats,
  suggestionsLoading,
  onRestoreSuccess,
  onImportCommitSuccess,
  onAutoFormulate,
  autoFormulateDisabled = false,
  autoFormulateDisabledReason,
}: FlaskToolbarProps) {
  return (
    <div className="hstack gap-2" style={{ position: "relative" }}>
      {/* 1 · Auto-Formulate — proprietary primary action. */}
      <AutoFormulateButton
        disabled={autoFormulateDisabled}
        disabledReason={autoFormulateDisabledReason}
        onRun={onAutoFormulate}
      />

      {/* Visual separator between the proprietary action and the light controls. */}
      <span
        aria-hidden
        style={{
          width: 1,
          height: 16,
          background: "var(--line)",
          margin: "0 2px",
          alignSelf: "center",
          flexShrink: 0,
        }}
      />

      {/* 2 · Composition — toggle, disabled with a tooltip when the active
          flavor has no TARGET food. */}
      {onToggleComposition && (
        <ToolbarBtn
          icon="atom"
          label="Composition"
          active={showComposition && hasComposition}
          disabled={!hasComposition}
          onClick={
            hasComposition
              ? () => {
                  logEvent("flavor_workbench_flask_tool_clicked", {
                    tool: "composition",
                    flavor_id: flavorId,
                    formula_id: formulaId,
                  });
                  onToggleComposition();
                }
              : undefined
          }
          title={
            hasComposition
              ? "Compare against the reference food's GC-MS composition"
              : "Assign a target flavor to see the composition"
          }
        />
      )}

      {/* 3 · Compare — popover with picker + selection state. */}
      {compareCatalog && onCompareToggle && onCompareClear && computeCompareStats && (
        <CompareControl
          catalog={compareCatalog}
          selected={compareSelected}
          onToggle={onCompareToggle}
          onClear={onCompareClear}
          computeStats={computeCompareStats}
          currentFormulation={formulation}
          suggestionsLoading={suggestionsLoading}
          flavorId={flavorId}
          formulaId={formulaId}
        />
      )}

      {/* 4 · ⋯ Overflow — History, Import, Export. */}
      <FlaskMoreMenu
        formulation={formulation}
        foundation={foundation}
        flavorId={flavorId}
        formulaId={formulaId}
        flavorName={flavorName}
        formulaName={formulaName}
        onRestoreSuccess={onRestoreSuccess}
        onImportCommitSuccess={onImportCommitSuccess}
      />
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────

type ToolbarBtnProps = {
  icon: Parameters<typeof Icon>[0]["name"];
  label: string;
  active?: boolean;
  title?: string;
  disabled?: boolean;
  onClick?: () => void;
};

function ToolbarBtn({
  icon,
  label,
  active = false,
  title,
  disabled = false,
  onClick,
}: ToolbarBtnProps) {
  const litStyle = active
    ? {
        background: "var(--brand-green)",
        color: "var(--brand-cream)",
        borderColor: "var(--brand-green)",
        fontWeight: 500 as const,
      }
    : undefined;
  return (
    <button
      type="button"
      title={title || label}
      onClick={onClick}
      disabled={disabled}
      className="btn xs"
      style={litStyle}
    >
      <Icon name={icon} size={11} />
      <span>{label}</span>
    </button>
  );
}
